import { type ReactNode } from "react";
import t from "@/lang/lang";
import { useParams } from "react-router";
import { useQpmOfferShowQuery } from "@/services/backend/qpm/offers/api/offer-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Separator } from "@/shared/components/ui/separator";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { OfferDocumentProfileEditor } from "@/routes/processes/[processId]/qpm/offers/[offerId]/overview/_components/offer-document-profile-editor";
import { OfferDateSelector } from "@/routes/processes/[processId]/qpm/offers/[offerId]/overview/_components/offer-date-selector";
import { OfferValidUntilDateSelector } from "@/routes/processes/[processId]/qpm/offers/[offerId]/overview/_components/offer-valid-until-date-selector";
import { InstallationTableComponent } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/installation-table-component";
import { HirePurchaseTableComponent } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/hire-purchase-table-component";
import { MaintenanceTableComponent } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/maintenance-table-component";
import { OfferFlyExterminatorMaintenanceComponent } from "@/routes/processes/[processId]/qpm/offers/[offerId]/overview/_components/offer-fly-exterminator-maintenance-component";
import { RentingTableComponent } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/renting-table-component";
import { cn } from "@/shared/lib/utils";
import { PriceVisibilitySettings } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/price-visibility-settings";
import {
  hasVisibleFlyExterminatorMaintenanceSection,
  hasVisibleInstallationSection,
  hasVisibleMaintenanceSection,
  hasVisibleRentingSection,
} from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/price-visibility";

type OverviewSectionProps = {
  title: string;
  description?: string;
  children: ReactNode;
  className?: string;
};

function OverviewSection({
  title,
  description,
  children,
  className,
}: OverviewSectionProps) {
  return (
    <section
      className={cn("bg-background rounded-lg border p-4 sm:p-5", className)}
    >
      <div className="space-y-1 pb-3">
        <h3 className="text-base leading-tight font-semibold">{title}</h3>
        {description ? (
          <p className="text-muted-foreground text-sm">{description}</p>
        ) : null}
      </div>
      {children}
    </section>
  );
}

type PriceTableSectionProps = {
  title: string;
  children: ReactNode;
};

function PriceTableSection({ title, children }: PriceTableSectionProps) {
  return (
    <section className="bg-card rounded-lg border">
      <div className="border-b px-4 py-3">
        <h4 className="text-sm font-semibold sm:text-base">{title}</h4>
      </div>
      <div className="overflow-x-auto px-2 py-2 sm:px-4 sm:py-3">
        {children}
      </div>
    </section>
  );
}

export function OfferOverviewComponent() {
  const params = useParams();
  const { offerId } = params;
  const {
    data: offer,
    isLoading,
    error,
  } = useQpmOfferShowQuery({ id: offerId! }, { skip: !offerId });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="space-y-3 px-4 py-4 sm:px-6">
          <Skeleton className="h-7 w-64" />
          <Separator />
        </CardHeader>
        <CardContent className="space-y-4 px-4 pb-6 sm:px-6">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-52 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!offer) {
    return null;
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader className="bg-muted/20 space-y-3 px-4 py-4 sm:px-6">
        <CardTitle className="text-lg font-semibold sm:text-xl">
          {t("Angebot")}
          {" - "}
          <DocumentNumberById
            className="inline text-lg sm:text-xl"
            documentId={offer.id}
          />
        </CardTitle>
        <Separator />
      </CardHeader>

      <CardContent className="space-y-5 px-4 py-5 sm:px-6">
        <OverviewSection title={t("Angebotsart und Dokumentprofil")}>
          <div className="space-y-4">
            <OfferDocumentProfileEditor offer={offer} />
            <Separator />
            <div className="grid gap-4 lg:grid-cols-2">
              <OfferDateSelector offer={offer} />
              <OfferValidUntilDateSelector offer={offer} />
            </div>
          </div>
        </OverviewSection>

        <OverviewSection
          title={t("Preisübersicht")}
          description={t(
            "Preispositionen können einzeln ein oder ausgeblendet werden.",
          )}
          className="space-y-4"
        >
          <PriceVisibilitySettings offer={offer} />

          {hasVisibleInstallationSection(offer) ? (
            <PriceTableSection title={t("Einrichtung und Installation")}>
              <InstallationTableComponent offer={offer} />
            </PriceTableSection>
          ) : null}

          <PriceTableSection title={t("Mietkauf")}>
            <HirePurchaseTableComponent offerId={offer.id} />
          </PriceTableSection>

          {hasVisibleMaintenanceSection(offer) ? (
            <PriceTableSection title={t("Betriebs- und Serviceleistungen")}>
              <MaintenanceTableComponent offer={offer} />
            </PriceTableSection>
          ) : null}

          {hasVisibleFlyExterminatorMaintenanceSection(offer) ? (
            <PriceTableSection title={t("Wartung Fluginsektenvernichter")}>
              <OfferFlyExterminatorMaintenanceComponent offer={offer} />
            </PriceTableSection>
          ) : null}

          {hasVisibleRentingSection(offer) ? (
            <PriceTableSection title={t("Miete ALoRa")}>
              <RentingTableComponent offer={offer} />
            </PriceTableSection>
          ) : null}
        </OverviewSection>
      </CardContent>
    </Card>
  );
}
